import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { BaseCustomDialog } from '../base-custom-dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { CONTACTS_DATA_SERVICE, ContactsDataService } from '../../../../services/contacts/contacts-data.service';
import { ContactFormComponent } from '../../components/contact-form/contact-form.component';
import { PhoneNumberHelper } from '../../../../helper/phone-number.helper';
import { HideLoader, ShowLoader } from "../../../store/loader/loader.actions";
import { Store } from "@ngxs/store";
import { ValidatorHelper } from '../../../../helper/validator.helper';
import { LeadHelper } from '../../../../helper/lead.helper';
import { ContactHelper } from '../../../../helper/contact.helper';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../../../../services/leads/leads-data.service';
import { CreateLead } from '../../../../models/leads/leads.model';
import { ACTION_DATA_SERVICE, ActionDataService } from '../../../../services/action/data.service';

@Component({
  selector: 'fi-edit-contact',
  templateUrl: './edit-contact.component.html',
  styleUrls: ['./edit-contact.component.scss'],
  standalone: false,
})

export class EditContactComponent extends BaseCustomDialog implements OnInit, AfterViewInit {
  loadedContact = false;
  additionalContact = false;
  compendiumActionEnabled = false;
  compendiumVisible = false;

  contactForm = this.fb.group({
    Id: this.fb.control(null),
    ContactTypeId: this.fb.control(3),
    ContactMethodId: this.fb.control(1),
    Salutation: this.fb.control('', Validators.required),
    FirstName: this.fb.control('', [Validators.required, Validators.pattern(ValidatorHelper.nameValidator)]),
    LastName: this.fb.control('', [Validators.required, Validators.pattern(ValidatorHelper.nameValidator)]),
    JobTitle: this.fb.control('', [Validators.pattern(ValidatorHelper.positionValidator)]),
    Email: this.fb.control('', [Validators.email, Validators.pattern(ValidatorHelper.emailValidator)]),
    AdditionalEmail: this.fb.control('', [Validators.email, Validators.pattern(ValidatorHelper.emailValidator)]),
    Phone: this.fb.control('', [Validators.pattern(PhoneNumberHelper.phoneValidator)]),
    PhonePrefix: this.fb.control('', [Validators.pattern(PhoneNumberHelper.countryCodeValidator)]),
    Mobile: this.fb.control('', [Validators.pattern(PhoneNumberHelper.phoneValidator)]),
    PhoneOther: this.fb.control('', [Validators.pattern(PhoneNumberHelper.phoneValidator)]),
    MobilePrefix: this.fb.control('', [Validators.pattern(PhoneNumberHelper.countryCodeValidator)]),
    PhoneOtherPrefix: this.fb.control('', [Validators.pattern(PhoneNumberHelper.countryCodeValidator)]),
    LanguageCode: this.fb.control('', Validators.required),
    Company: this.fb.control('', Validators.pattern(ValidatorHelper.companyValidator)),
    Address: this.fb.control('', Validators.pattern(ValidatorHelper.addressValidator)),
    TaxNumber: this.fb.control('', Validators.pattern(ValidatorHelper.taxNumberValidator)),
    AddressExt: this.fb.control('', Validators.pattern(ValidatorHelper.addressValidator)),
    ZipCode: this.fb.control('', Validators.pattern(ValidatorHelper.zipValidator)),
    City: this.fb.control('', Validators.pattern(ValidatorHelper.cityValidator)),
    ProvinceCode: this.fb.control(''),
    CountryCode: this.fb.control(''),
    VatNumber: this.fb.control('', Validators.pattern(ValidatorHelper.vatNumberValidator)),
    Website: this.fb.control('', Validators.pattern(ValidatorHelper.urlValidator)),
    Title: this.fb.control(''),
    CustomerCodeComposer: this.fb.control(''),
    CustomerCode: this.fb.control(''),
  });

  requestForm = this.fb.group({
    LeadTypeId: this.fb.control(null),
    LeadSourceId: this.fb.control(null),
    LeadText: this.fb.control('')
  });

  @ViewChild(ContactFormComponent) contactFormComponent: ContactFormComponent;

  private shouldCreateLead = false;
  private leadId: number;

  constructor(
    public dialogRef: MatDialogRef<EditContactComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: {
      id: number;
      email: string;
      leadTypeId: number;
      additionalContact?: boolean;
      compendiumHasAccess?: boolean;
      compendiumActionEnabled?: boolean;
      compendiumActionVisible?: boolean;
    },
    @Inject(CONTACTS_DATA_SERVICE) private contactsDataService: ContactsDataService,
    @Inject(ACTION_DATA_SERVICE) private actionDataService: ActionDataService,
    @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
    private fb: UntypedFormBuilder,
    private store: Store,
  ) {
    super(dialogRef);
    this.store.dispatch(new ShowLoader());

    this.additionalContact = dialogData.additionalContact ?? false;
    this.compendiumActionEnabled = dialogData.compendiumActionEnabled ?? false;
    this.compendiumVisible = dialogData.compendiumActionVisible ?? false;

    const observable = dialogData.id ?
      this.contactsDataService.getContacts(dialogData.id) :
      this.contactsDataService.getContactsByEmail(dialogData.email);

    observable.subscribe(( { success, data } ) => {
      this.loadedContact = true;
      this.store.dispatch(new HideLoader());

      if ( success ) {
        this.leadId = data.ManagementLeadId;
        if ( this.additionalContact ) {
          this.contactForm.controls.ContactTypeId.setValue(data.ContactTypeId);
          this.contactForm.controls.Company.setValue(data.Company);
          this.contactForm.controls.Address.setValue(data.Address);
          this.contactForm.controls.VatNumber.setValue(data.VatNumber);
          this.contactForm.controls.Website.setValue(data.Website);
          this.contactForm.controls.AddressExt.setValue(data.AddressExt);
          this.contactForm.controls.ZipCode.setValue(data.ZipCode);
          this.contactForm.controls.City.setValue(data.City);
          this.contactForm.controls.ProvinceCode.setValue(data.ProvinceCode);
          this.contactForm.controls.CountryCode.setValue(data.CountryCode);
        } else {
          this.contactForm.patchValue(data);
          this.contactForm.controls.Salutation.setValue(data.SalutationId);
        }


        setTimeout(() => {
          if (!this.additionalContact) {
            this.contactFormComponent.consentMarketing = data.ConsentMarketing;
            this.contactFormComponent.consentThirdParties = data.ConsentThirdParties;
          }
          this.contactFormComponent.accessCompendium = dialogData?.compendiumHasAccess;
        }, 5)
      }
    });

    this.valueChanges();
  }

  valueChanges(): void {
    const leadTypeId = this.dialogData.leadTypeId;

    for ( const fieldName of ['ZipCode', 'City', 'Address', 'CountryCode'] ) {
      if ( leadTypeId === LeadHelper.LEAD_TYPE_BROCHURE || leadTypeId === LeadHelper.LEAD_TYPE_MUSTER || leadTypeId === LeadHelper.LEAD_TYPE_AUSSCHREIBUNGSTEXTE || leadTypeId === LeadHelper.LEAD_TYPE_CAD_DATEN ) {
        this.setValidators(fieldName);
      } else {
        this.clearValidators(fieldName);
      }

      this.contactForm.controls[fieldName].updateValueAndValidity();
    }
  }

  clearValidators( fieldName: string ): void {
    switch ( fieldName ) {
      case 'ZipCode':
        this.contactForm.controls[fieldName].setValidators([Validators.pattern(ValidatorHelper.zipValidator)]);
        break;
      case 'City':
        this.contactForm.controls[fieldName].setValidators([Validators.pattern(ValidatorHelper.cityValidator)]);
        break;
      case 'Address':
        this.contactForm.controls[fieldName].setValidators([Validators.pattern(ValidatorHelper.addressValidator)]);
        break;
      default:
        this.contactForm.controls[fieldName].clearValidators();
        break;
    }
  }

  setValidators( fieldName: string ): void {
    switch ( fieldName ) {
      case 'ZipCode':
        this.contactForm.controls[fieldName].setValidators([Validators.required, Validators.pattern(ValidatorHelper.zipValidator)]);
        break;
      case 'City':
        this.contactForm.controls[fieldName].setValidators([Validators.required, Validators.pattern(ValidatorHelper.cityValidator)]);
        break;
      case 'Address':
        this.contactForm.controls[fieldName].setValidators([Validators.required, Validators.pattern(ValidatorHelper.addressValidator)]);
        break;
      default:
        this.contactForm.controls[fieldName].setValidators(Validators.required);
        break;
    }
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
  }

  onClosePopup( message?: string ): void {
    this.clearClassAndCloseDialog(this.dialogRef, message);
  }

  onSubmit(): void {

    this.shouldCreateLead = !this.contactFormComponent.emailExists && this.additionalContact;

    if (
      this.contactForm.valid &&
      !this.contactFormComponent.isInvalidProvinceCode(true) &&
      !this.contactFormComponent.checkIfEmailOrPhoneNeedsToBeFilled()
    ) {

      this.store.dispatch(new ShowLoader());

      if ( this.shouldCreateLead ) {
        this.createLead();
      } else {
        this.updateContact();
      }

    } else {
      this.contactFormComponent.showErrors = true;
      this.contactFormComponent.scrollToInvalidContactField();
    }
  }

  getBody(): any {
    const contactData = this.contactForm.getRawValue();

    if ( !contactData.Email && contactData.ContactMethodId === 1 ) {
      contactData.ContactMethodId = undefined;
    }

    if ( !contactData.Phone && contactData.ContactMethodId === 2 ) {
      contactData.ContactMethodId = undefined;
    }

    if ( !contactData.Mobile && contactData.ContactMethodId === 3 ) {
      contactData.ContactMethodId = undefined;
    }

    if ( contactData.Company && contactData.ContactTypeId === 3 ) {
      contactData.Company = undefined;
    }

    if ( !contactData.ContactMethodId ) {
      if ( contactData.Email ) {
        contactData.ContactMethodId = 1;
      } else if ( contactData.Phone ) {
        contactData.ContactMethodId = 2;
      } else if ( contactData.Mobile ) {
        contactData.ContactMethodId = 3;
      } else {
        contactData.ContactMethodId = 1;
      }
    }

    return contactData;
  }

  private createLead(): void {

    const requestBody: CreateLead = {
      Contact: {
        ...this.getBody(),
        SalutationId: +this.contactForm.controls.Salutation.value,
        PhonePrefix: this.contactForm.controls.Phone.value?.trim() ? this.contactForm.controls.PhonePrefix.value : null,
        MobilePrefix: this.contactForm.controls.Mobile.value?.trim() ? this.contactForm.controls.MobilePrefix.value : null,
        ConsentMarketing: this.contactFormComponent.consentMarketing,
        ConsentThirdParties: this.contactFormComponent.consentThirdParties,
      },
      LeadTypeId: LeadHelper.LEAD_TYPE_PLANER_RELATIONSHIP,
      LeadSourceId: LeadHelper.LEAD_SOURCE_TELEFON,
    };

    this.leadsDataService.createLead(requestBody).subscribe(() => {
      this.closeEditWithUpdate();
    });

  }

  private updateContact(): void {
    const requestBody = {
      ...this.getBody(),
      SalutationId: +this.contactForm.controls.Salutation.value,
      PhonePrefix: this.contactForm.controls.Phone.value?.trim() ? this.contactForm.controls.PhonePrefix.value : null,
      MobilePrefix: this.contactForm.controls.Mobile.value?.trim() ? this.contactForm.controls.MobilePrefix.value : null,
      ConsentMarketing: this.contactFormComponent.consentMarketing,
      ConsentThirdParties: this.contactFormComponent.consentThirdParties,
    };

    this.contactsDataService.editContact(requestBody).subscribe(() => {

      if ( this.compendiumVisible && this.compendiumActionEnabled && this.contactFormComponent.accessCompendium !== this.dialogData?.compendiumHasAccess ) {
        this.updateCompendiumIfNeeded();
      } else {
        this.closeEditWithUpdate();
      }
    });
  }

  private updateCompendiumIfNeeded(): void {

    if ( !this.leadId ) {
      this.closeEditWithUpdate();
      return;
    }
    const observable = this.contactFormComponent.accessCompendium ?
      this.actionDataService.compendiumAllow(
        this.leadId,
        '',
      ) :
      this.actionDataService.compendiumDeny(
        this.leadId,
        '',
      );

    observable.subscribe(() => {
      this.closeEditWithUpdate();
    });
  }

  private closeEditWithUpdate(): void {
    this.onClosePopup('update');
    this.store.dispatch(new HideLoader());
  }

  protected readonly ContactHelper = ContactHelper;
}
